// The visitor's live channel to a support agent's reply (spec §7,
// docs/superpowers/specs/2026-09-02-human-takeover-tickets-design.md).
//
// openConversationStream() is called once a conversation AND its client
// token exist — never gated on a response carrying human_state. An idle
// visitor who already received their last bot reply will not call
// send() again, so if the stream only opened on a human_state-bearing
// response, a staff reply would sit in Firestore unseen. The caller
// (chatmerce-chat.ts) is responsible for calling this early; this
// module just keeps the channel alive once asked to.
//
// Two delivery paths, matched to the server (apps/api/internal/handler/
// public_stream.go):
//
//   - a WebSocket that the server pushes RoleHuman messages and
//     ticket-state changes into as they happen;
//   - REST catch-up (GET .../messages?after=), run on every (re)connect
//     — including the very first — to recover whatever the socket
//     missed. The socket WILL close periodically (Cloud Run's request
//     cap, the server's own configured lifetime); that is routine, not
//     an error, so closing it never surfaces anything to the caller
//     beyond onMessage/onState/onTokenInvalid.
//
// Every network primitive (WebSocket, fetch, the reconnect timer) is
// injectable so tests never touch a real socket, network, or clock.

/** One message as the visitor's browser needs it. */
export type StreamMessage = {
  id: string;
  role: string;
  text: string;
  createdAt: string;
  authorName?: string;
  deliveryStatus?: string;
};

/** The conversation's ticket mirror, as carried by "state" frames and
 * the REST catch-up response alike. */
export type StreamStateUpdate = {
  humanState: string;
  activeTicketId: string;
};

// WebSocketLike is the subset of the real WebSocket a test fake needs
// to implement — no readyState, no binary framing, because the widget
// never sends anything on this socket (spec §7: it posts to /chat over
// plain HTTP; this channel is receive-only).
export interface WebSocketLike {
  onopen: ((ev: unknown) => void) | null;
  onclose: ((ev: { code: number; reason: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: string }) => void) | null;
  close(code?: number, reason?: string): void;
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface OpenStreamOptions {
  // widget_key is REQUIRED on both endpoints whenever the embed has
  // one: conv.Conversation carries no widget binding, so without it
  // the per-widget origin allowlist is enforced in code but inert.
  widgetKey?: string;
  // onTokenInvalid fires ONLY for the exact 403 body "invalid client
  // token" (server: public_stream.go refuse()) — never for "origin not
  // allowed" or "widget not found", which are misconfiguration, not a
  // bad token. Branching on status alone would make either
  // misconfiguration discard a perfectly good conversation.
  onTokenInvalid?: () => void;
  wsFactory?: (url: string, protocols: string[]) => WebSocketLike;
  fetchImpl?: FetchLike;
  // backoffSchedule(attempt) returns the delay (ms) before reconnect
  // attempt N (0-indexed). Reset to 0 once a socket has stayed open
  // long enough to be considered stable (OPEN_STABLE_MS) — NOT on a
  // successful catch-up (a catch-up says nothing about whether the
  // socket that cycle is about to open will connect) and NOT the
  // instant onopen fires either (Fix Round 2, Item 2): the hub
  // deliberately disconnects a subscriber that falls behind (spec §7),
  // an "opens then closes right away" pattern, so resetting immediately
  // on onopen made that reachable path reconnect at a near-zero delay
  // forever.
  backoffSchedule?: (attempt: number) => number;
  setTimeoutImpl?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimeoutImpl?: (id: ReturnType<typeof setTimeout>) => void;
}

const DEFAULT_BACKOFF_MS = [1000, 2000, 5000, 10000, 20000, 30000];

// How long a socket has to stay open before it counts as "actually
// worked" and resets the backoff counter (Fix Round 2, Item 2). Not
// onopen itself — see the comment on backoffSchedule above.
const OPEN_STABLE_MS = 3000;

// Equal jitter (Fix Round 2, Item 2 — full jitter's zero floor was
// itself a bug): a Cloud Run instance recycle drops every visitor
// socket it was holding at once, and a purely deterministic schedule
// means all of them reconnect at exactly the same millisecond — a
// thundering herd against the socket AND the REST catch-up it runs
// first. Full jitter (round 1's fix) solved that but could return
// anything down to ~0ms, which combined with an immediate onopen reset
// meant a socket that opens and is instantly dropped (the hub's own
// slow-consumer disconnect — routine, not hypothetical) could reconnect
// near-instantly forever. Equal jitter keeps half the cap as a floor —
// [cap/2, cap) — so the schedule always grows with `attempt` (the
// property the round 1 test pins) while never approaching zero.
export function defaultBackoff(attempt: number): number {
  const cap = DEFAULT_BACKOFF_MS[Math.min(attempt, DEFAULT_BACKOFF_MS.length - 1)];
  const floor = cap / 2;
  return Math.floor(floor + Math.random() * floor);
}

// The exact refusal body that means "the token itself is bad" (spec
// §7/§8; server: public_stream.go refuse()). Every other refusal —
// "origin not allowed", "widget not found", a network blip, a 5xx — is
// transient or a tenant misconfiguration, not a reason to discard the
// visitor's conversation, so only this exact string stops the client
// from retrying.
const REFUSAL_INVALID_TOKEN = "invalid client token";

// Wire shapes exactly as the server emits them (public_stream.go:
// publicStreamMessage, streamFrame, catchUpResponse). Kept private to
// this module — the rest of the widget sees only StreamMessage /
// StreamStateUpdate, so a server field rename shows up as one diff
// here, not scattered through chatmerce-chat.ts.
interface RawMessage {
  id: string;
  role: string;
  content: string;
  created_at: string;
  author_name?: string;
  delivery_status?: string;
}
interface RawFrame {
  type: string;
  message?: RawMessage;
  human_state: string;
  active_ticket_id: string;
}
interface RawCatchUp {
  messages: RawMessage[];
  human_state: string;
  active_ticket_id: string;
}

function toStreamMessage(m: RawMessage): StreamMessage {
  return {
    id: m.id,
    role: m.role,
    text: m.content,
    createdAt: m.created_at,
    authorName: m.author_name,
    deliveryStatus: m.delivery_status,
  };
}

/**
 * Opens the visitor's stream for one conversation. Returns a close()
 * function that tears down the socket and cancels any pending
 * reconnect — call it when the widget disconnects or starts a new
 * conversation.
 */
export function openConversationStream(
  apiBase: string,
  slug: string,
  convID: string,
  token: string,
  onMessage: (m: StreamMessage) => void,
  onState: (s: StreamStateUpdate) => void,
  opts: OpenStreamOptions = {},
): () => void {
  const fetchImpl = opts.fetchImpl ?? fetch;
  const wsFactory =
    opts.wsFactory ??
    ((url: string, protocols: string[]) =>
      new WebSocket(url, protocols) as unknown as WebSocketLike);
  const backoffSchedule = opts.backoffSchedule ?? defaultBackoff;
  const setTimeoutImpl =
    opts.setTimeoutImpl ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearTimeoutImpl =
    opts.clearTimeoutImpl ?? ((id: ReturnType<typeof setTimeout>) => clearTimeout(id));

  const base = apiBase.replace(/\/$/, "");
  // The WebSocket constructor throws a SyntaxError for anything but a
  // ws:/wss: URL — it does NOT accept http:/https: the way fetch does —
  // so the socket needs its own base with the scheme swapped. The REST
  // catch-up keeps using `base` unchanged.
  const wsBase = base.replace(/^https:/i, "wss:").replace(/^http:/i, "ws:");
  const path = `/api/v1/public/tenants/${encodeURIComponent(slug)}/conversations/${encodeURIComponent(convID)}`;

  let closed = false;
  let attempt = 0;
  // The newest message timestamp seen so far, RFC3339Nano, exactly as
  // the server sent it — forwarded back verbatim as `after`. Starts
  // empty, which the server treats as "from the beginning": the first
  // catch-up on a fresh connect returns the whole (bounded) transcript,
  // which is what lets a returning visitor's reload rebuild it.
  let cursor = "";
  let socket: WebSocketLike | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  // Armed on onopen, cleared on any close/teardown before it fires —
  // see OPEN_STABLE_MS.
  let openStableTimer: ReturnType<typeof setTimeout> | null = null;

  function clearOpenStableTimer(): void {
    if (openStableTimer) {
      clearTimeoutImpl(openStableTimer);
      openStableTimer = null;
    }
  }
  // Bounded FIFO of recently-delivered message ids, mirroring the
  // server's own seenMessageIDs (public_stream.go) — a message that
  // arrives on both the catch-up and the socket (the overlap window
  // guarantees this — spec §7) is only ever delivered once. Bounded
  // (Fix Round 1 minor) because a stream can outlive many reconnects
  // and messages over its life, and an unbounded Set would grow for as
  // long as the tab stays open.
  const SEEN_MAX = 64;
  const seenOrder: string[] = [];
  const seen = new Set<string>();

  // markSeen reports whether id is new. An empty id — which no real
  // message has — is always treated as new, matching the server's own
  // rule (public_stream.go's seenMessageIDs.add).
  function markSeen(id: string): boolean {
    if (!id) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    seenOrder.push(id);
    if (seenOrder.length > SEEN_MAX) {
      const oldest = seenOrder.shift();
      if (oldest !== undefined) seen.delete(oldest);
    }
    return true;
  }

  function widgetKeyParam(): string {
    return opts.widgetKey ? `widget_key=${encodeURIComponent(opts.widgetKey)}` : "";
  }

  function messagesURL(): string {
    const params: string[] = [];
    if (cursor) params.push(`after=${encodeURIComponent(cursor)}`);
    const wk = widgetKeyParam();
    if (wk) params.push(wk);
    return `${base}${path}/messages${params.length ? `?${params.join("&")}` : ""}`;
  }

  function streamURL(): string {
    const wk = widgetKeyParam();
    return `${wsBase}${path}/stream${wk ? `?${wk}` : ""}`;
  }

  function bumpCursor(createdAt: string): void {
    if (!createdAt) return;
    const parsed = Date.parse(createdAt);
    // A malformed timestamp must never freeze the cursor at whatever it
    // was — that would make every future catch-up re-request the whole
    // transcript from the same stale `after` forever (Fix Round 1 minor).
    if (Number.isNaN(parsed)) return;
    if (!cursor || parsed > Date.parse(cursor)) cursor = createdAt;
  }

  // safeCall isolates the caller's callback from this module's own
  // control flow (Fix Round 1 minor): both onMessage and onState run
  // inside an async catch-up loop and inside a synchronous socket event
  // handler alike, and in the former case an uncaught throw would
  // reject connectCycle's promise, which is invoked as
  // `void connectCycle()` — an unhandled rejection, with the reconnect
  // loop silently dead from then on over what could be a caller-side
  // rendering bug.
  function safeCall(fn: () => void): void {
    try {
      fn();
    } catch (err) {
      // eslint-disable-next-line no-console -- last resort visibility;
      // this module has no logger of its own.
      console.error("chatmerce stream: callback threw", err);
    }
  }

  function deliverMessage(m: StreamMessage): void {
    if (!markSeen(m.id)) return;
    bumpCursor(m.createdAt);
    safeCall(() => onMessage(m));
  }

  function teardownSocket(): void {
    clearOpenStableTimer();
    if (!socket) return;
    const s = socket;
    socket = null;
    s.onopen = null;
    s.onclose = null;
    s.onerror = null;
    s.onmessage = null;
    try {
      s.close();
    } catch {
      /* already closed */
    }
  }

  function scheduleReconnect(): void {
    if (closed) return;
    const delay = backoffSchedule(attempt);
    attempt += 1;
    timer = setTimeoutImpl(() => {
      timer = null;
      void connectCycle();
    }, delay);
  }

  type CatchUpResult = "ok" | "retry" | "stop";

  async function runCatchUp(): Promise<CatchUpResult> {
    let res: Response;
    try {
      res = await fetchImpl(messagesURL(), {
        headers: { "X-Chatmerce-Client-Token": token },
      });
    } catch {
      return closed ? "stop" : "retry";
    }
    if (closed) return "stop";
    if (!res.ok) {
      if (res.status === 403) {
        let body = "";
        try {
          body = (await res.text()).trim();
        } catch {
          // A body-read failure is a transient error, not a verdict —
          // fall through to "retry" rather than risk an unhandled
          // rejection breaking the reconnect loop for good.
        }
        if (closed) return "stop";
        if (body === REFUSAL_INVALID_TOKEN) {
          opts.onTokenInvalid?.();
          return "stop";
        }
      }
      // "origin not allowed", "widget not found", a rate limit, a 5xx —
      // none of these mean the token is bad, so keep retrying with
      // backoff rather than discarding the conversation.
      return "retry";
    }
    let data: RawCatchUp;
    try {
      data = (await res.json()) as RawCatchUp;
    } catch {
      return closed ? "stop" : "retry";
    }
    if (closed) return "stop";
    for (const raw of data.messages ?? []) {
      deliverMessage(toStreamMessage(raw));
    }
    safeCall(() =>
      onState({ humanState: data.human_state ?? "", activeTicketId: data.active_ticket_id ?? "" }),
    );
    // NOT `attempt = 0` here (Fix Round 1, Item 3): a successful
    // catch-up says nothing about whether the socket this cycle is
    // about to open will actually connect. The reset lives in
    // openSocket()'s onopen handler instead — see OPEN_STABLE_MS there
    // for why it isn't the instant onopen fires either (Fix Round 2,
    // Item 2).
    return "ok";
  }

  function openSocket(): void {
    if (closed) return;
    let sock: WebSocketLike;
    try {
      sock = wsFactory(streamURL(), ["chatmerce.v1", token]);
    } catch {
      scheduleReconnect();
      return;
    }
    socket = sock;
    // Arms the stability timer rather than resetting `attempt`
    // immediately (Fix Round 2, Item 2): onopen alone doesn't mean the
    // connection is healthy, only that it was accepted — the hub's own
    // slow-consumer disconnect (spec §7) looks exactly like "opens then
    // closes right away", and resetting here unconditionally made that
    // reconnect at a near-zero delay forever. Only a socket that stays
    // up for OPEN_STABLE_MS resets the counter; onclose below cancels
    // this timer the moment the socket drops, stable or not.
    sock.onopen = () => {
      clearOpenStableTimer();
      openStableTimer = setTimeoutImpl(() => {
        openStableTimer = null;
        attempt = 0;
      }, OPEN_STABLE_MS);
    };
    sock.onmessage = (ev) => {
      let frame: RawFrame;
      try {
        frame = JSON.parse(ev.data) as RawFrame;
      } catch {
        return;
      }
      if (frame.type === "message" && frame.message) {
        deliverMessage(toStreamMessage(frame.message));
      } else if (frame.type === "state") {
        safeCall(() =>
          onState({
            humanState: frame.human_state ?? "",
            activeTicketId: frame.active_ticket_id ?? "",
          }),
        );
      }
    };
    sock.onerror = () => {
      // onclose always follows onerror for a WebSocket that fails to
      // open or drops — that single handler owns the reconnect
      // decision, so there is nothing to do here.
    };
    sock.onclose = () => {
      // A closed socket means "reconnect and catch up", never "the
      // conversation ended" — the server's hub deliberately disconnects
      // a subscriber that falls behind for exactly this reason (spec
      // §7), and Cloud Run's own request cap closes every socket
      // eventually regardless. Routine, so nothing is surfaced as an
      // error here.
      //
      // The stability timer above must not survive this socket: if it
      // fired later (it can't — clearTimeoutImpl below prevents that —
      // but if this ordering ever changes) it would reset `attempt` for
      // a connection that already proved itself unstable by closing.
      clearOpenStableTimer();
      socket = null;
      scheduleReconnect();
    };
  }

  async function connectCycle(): Promise<void> {
    if (closed) return;
    const result = await runCatchUp();
    if (closed) return;
    if (result === "stop") {
      closed = true;
      return;
    }
    if (result === "retry") {
      scheduleReconnect();
      return;
    }
    openSocket();
  }

  void connectCycle();

  return function close(): void {
    closed = true;
    if (timer) {
      clearTimeoutImpl(timer);
      timer = null;
    }
    teardownSocket();
  };
}
